import { Building2, Loader2 } from 'lucide-react';
import type { ProfileData } from '@/features/profile/api/useProfileQueries';
import type { NormalizedField } from '@/features/auth/types/onboardingFields';
import { KYC } from '@/features/auth/types/onboardingFields';
import { fieldKey } from '@/features/auth/utils/onboardingFields';
import { KycDetailsCard } from '@/features/auth/components/KycDetailsCard';
import {
  AADHAAR_MAX_LENGTH,
  GST_MAX_LENGTH,
  PAN_MAX_LENGTH,
  detectKycFieldKind,
  sanitizeAadhaarInput,
  sanitizeGstInput,
  sanitizePanInput,
} from '@/features/team/utils/kycInputMask';
import { Field } from '../../ProfilePagePrimitives';

type KycRow = { label: string; value?: string | null };

type OtherDetailsSectionProps = {
  visibleFields: NormalizedField[];
  user?: ProfileData;
  dynamicForm: Record<string, string>;
  editing: boolean;
  setDynamicForm: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  fieldErrors?: Record<string, string>;
  onClearFieldError?: (name: string) => void;
  shouldShowVerifyButton?: (name: string) => boolean;
  isKycField?: (name: string) => boolean;
  isFieldLocked?: (name: string) => boolean;
  unlockVerifiedField?: (name: string) => void;
  isVerifyDisabled?: (name: string) => boolean;
  onVerifyPan?: (name: string) => void;
  onVerifyGst?: (name: string) => void;
  verifyPanPending?: boolean;
  verifyGstPending?: boolean;
  panRows?: KycRow[];
  gstRows?: KycRow[];
};

function sanitizeForField(field: NormalizedField, raw: string): string {
  const kind = detectKycFieldKind(
    field.fieldName,
    field.fieldType,
    field.fieldLabel,
  );
  if (kind === 'pan') return sanitizePanInput(raw);
  if (kind === 'gst') return sanitizeGstInput(raw);
  if (kind === 'aadhaar') return sanitizeAadhaarInput(raw);
  return raw;
}

function maxLenForField(field: NormalizedField): number | undefined {
  const kind = detectKycFieldKind(
    field.fieldName,
    field.fieldType,
    field.fieldLabel,
  );
  if (kind === 'pan') return PAN_MAX_LENGTH;
  if (kind === 'gst') return GST_MAX_LENGTH;
  if (kind === 'aadhaar') return AADHAAR_MAX_LENGTH;
  const max = field.validation?.maxLength ?? field.validation?.max;
  return max != null ? Number(max) : undefined;
}

export function OtherDetailsSection({
  visibleFields,
  user,
  dynamicForm,
  editing,
  setDynamicForm,
  fieldErrors = {},
  onClearFieldError,
  shouldShowVerifyButton,
  isKycField,
  isFieldLocked,
  unlockVerifiedField,
  isVerifyDisabled,
  onVerifyPan,
  onVerifyGst,
  verifyPanPending,
  verifyGstPending,
  panRows = [],
  gstRows = [],
}: OtherDetailsSectionProps) {
  if (visibleFields.length === 0) return null;

  return (
    <section className="rounded-2xl border border-[var(--dash-line)] bg-white p-5 text-left shadow-[0_4px_20px_-10px_rgba(16,38,73,0.08)]">
      <div className="mb-4 flex items-center gap-2">
        <Building2 className="h-4 w-4 text-[var(--dash-brand)]" />
        <h3 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">
          Other Details
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        {visibleFields.map((f) => {
          const rawVal = user?.dynamicFields?.[f.fieldName];
          if (
            rawVal &&
            typeof rawVal === 'object' &&
            (rawVal as Record<string, unknown>).url
          ) {
            return (
              <div key={f.fieldName}>
                <label className="mb-1 block text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
                  {f.fieldLabel}
                </label>
                <img
                  src={(rawVal as Record<string, string>).url}
                  alt={f.fieldLabel}
                  className="h-20 w-auto rounded-xl border border-[var(--dash-line)] object-cover"
                />
              </div>
            );
          }

          const key = fieldKey(f.fieldName);
          const isPan = key === KYC.PAN;
          const isGst = key === KYC.GST;
          const kyc = Boolean(isKycField?.(f.fieldName));
          const locked = editing && Boolean(isFieldLocked?.(f.fieldName));
          const showVerify =
            editing && Boolean(shouldShowVerifyButton?.(f.fieldName));
          const verifying = isPan
            ? Boolean(verifyPanPending)
            : isGst
              ? Boolean(verifyGstPending)
              : false;
          const error = fieldErrors[f.fieldName];
          const maxLen = maxLenForField(f);
          const kind = detectKycFieldKind(
            f.fieldName,
            f.fieldType,
            f.fieldLabel,
          );

          if (!editing) {
            return (
              <Field
                key={f.fieldName}
                label={f.fieldLabel}
                value={dynamicForm[f.fieldName] || ''}
                editing={false}
                onChange={() => {}}
              />
            );
          }

          return (
            <div key={f.fieldName} className="sm:col-span-1">
              <label className="mb-1 block text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
                {f.fieldLabel}
                {f.isRequired ? (
                  <span className="ml-1 text-red-500" aria-hidden>
                    *
                  </span>
                ) : null}
              </label>
              <div className="flex items-center gap-2">
                <input
                  id={`profile-dynamic-${f.fieldName}`}
                  type="text"
                  value={dynamicForm[f.fieldName] || ''}
                  readOnly={locked}
                  maxLength={maxLen}
                  autoCapitalize={kind ? 'characters' : undefined}
                  spellCheck={kind ? false : undefined}
                  placeholder={f.placeholder || f.fieldLabel}
                  aria-invalid={Boolean(error)}
                  onChange={(e) => {
                    onClearFieldError?.(f.fieldName);
                    const next = sanitizeForField(f, e.target.value);
                    setDynamicForm((prev) => ({
                      ...prev,
                      [f.fieldName]: next,
                    }));
                  }}
                  className={`min-w-0 flex-1 rounded-xl border bg-[var(--dash-soft)] px-3 py-2.5 text-sm font-semibold text-[var(--dash-ink)] outline-none transition focus:border-[var(--dash-brand)] focus:bg-white ${
                    error
                      ? 'border-red-400'
                      : 'border-[var(--dash-line)]'
                  } ${locked ? 'cursor-default opacity-90' : ''}`}
                />
                {showVerify ? (
                  <button
                    type="button"
                    disabled={
                      verifying || Boolean(isVerifyDisabled?.(f.fieldName))
                    }
                    onClick={() =>
                      isPan
                        ? onVerifyPan?.(f.fieldName)
                        : onVerifyGst?.(f.fieldName)
                    }
                    className="dash-btn-gradient inline-flex h-[42px] shrink-0 items-center justify-center gap-1.5 rounded-xl px-3.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {verifying ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : null}
                    {verifying ? 'Verifying…' : 'Verify'}
                  </button>
                ) : null}
                {locked && kyc ? (
                  <button
                    type="button"
                    onClick={() => unlockVerifiedField?.(f.fieldName)}
                    className="inline-flex h-[42px] shrink-0 items-center justify-center rounded-xl border border-[var(--dash-brand)] bg-white px-3.5 text-xs font-semibold text-[var(--dash-brand)]"
                  >
                    Edit
                  </button>
                ) : null}
              </div>
              {error ? (
                <p className="m-0 mt-1 text-xs font-medium text-red-500" role="alert">
                  {error}
                </p>
              ) : null}
              {isPan && !showVerify ? (
                <div data-kyc-card="pan">
                  <KycDetailsCard title="PAN details" rows={panRows} />
                </div>
              ) : null}
              {isGst && !showVerify ? (
                <div data-kyc-card="gst">
                  <KycDetailsCard title="GST details" rows={gstRows} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
